import {useI18n} from 'vue-i18n';
import {PASSWORD_MAX_LENGTH} from '@/ts/composables/pure/use-password-requirements';

/** Returns a validation rule that checks if a string value is non-empty */
export function useRequiredRule(): (val: string) => string | true {
  const {t: $t} = useI18n();
  return (val: string) => {
    return val && (val?.trim()?.length ?? 0) > 0 || $t('required');
  };
}

/** Returns a validation rule that checks if a numeric value is not null */
export function useRequiredNumber(): (val: number) => string | true {
  const {t: $t} = useI18n();
  return (val: number) => {
    return val !== null || $t('required');
  };
}

/** Returns a pair of validation rules enforcing minimum birth year (1900) and minimum age (18) */
export function useYearsOfBirth(): [(val: number) => string | true, (val: number) => string | true] {
  const {t: $t} = useI18n();

  function minYear(val: number): string | true {
    const MIN_BIRTH_YEAR = 1900;
    return (val > MIN_BIRTH_YEAR && val < (new Date().getFullYear())) || $t('invalid_year');
  }

  function maxYear(val: number): string | true {
    // Min 18 years old
    return val < (new Date().getFullYear() - 17) || $t('you_must_be');
  }

  return [minYear, maxYear];
}

/** Returns a validation rule that checks if a select input has a non-empty value or selection */
export function useRequiredSelectRule(): (val: string[] | string) => string | true {
  const {t: $t} = useI18n();
  return (val: string[] | string) => {
    if ((Array.isArray(val) && val.length === 0) || !val) {
      return $t('required');
    }
    return true;
  };
}

/** Returns a validation rule that checks if a boolean value is truthy */
export function useRequiredTrueRule(): (val: boolean) => string | true {
  const {t: $t} = useI18n();
  return (val: boolean) => {
    return Boolean(val) || $t('required');
  };
}

/** Returns a validation rule that checks if a string matches the YYYY-MM-DD date format */
export function useRequiredDateRule(): (val: string) => string | true {
  const {t: $t} = useI18n();
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/u;
  return (val: string) => {
    return dateFormatRegex.test(val) || $t('required');
  };
}

/** Returns a validation rule that checks for a non-empty, valid email format */
export function useEmailRule(): (val: string) => string | true {
  const {t: $t} = useI18n();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
  // Validate the trimmed value: submission trims too, and mobile autocomplete
  // routinely pastes a trailing space that would otherwise read as invalid.
  return (val: string) => {
    const trimmed = val?.trim();
    if (!trimmed) {
      return $t('required');
    }

    if (!emailPattern.test(trimmed)) {
      return $t('invalid_email');
    }
    return true;
  };
}

/** Returns an array of password validation rules enforcing length, case, digit, and special character requirements */
export function usePasswordRules(): ((val: string) => string | true)[] {
  const {t: $t} = useI18n();
  return [
    (val: string): string | true => Boolean(val) || $t('password_required'),
    (val: string): string | true => (val?.length ?? 0) >= 8 || $t('password_required_8'),
    (val: string): string | true => (val?.length ?? 0) < PASSWORD_MAX_LENGTH || $t('password_required_less_70'),
    (val: string): string | true => /[A-Z]/u.test(val) || $t('password_required_upper'),
    (val: string): string | true => /[a-z]/u.test(val) || $t('password_required_lower'),
    (val: string): string | true => /\d/u.test(val) || $t('password_required_number'),
    (val: string): string | true => /[^A-Za-z0-9]/u.test(val) || $t('password_required_special'),
  ];
}
